import { useState } from "react";
import { format } from "date-fns";

import { cn } from "@/libs/utils";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CommonImg from "@/components/Common/Img";

import CalendarIcon from "@/assets/images/calendar-icon.png";

export type DateRangeType = {
  from: Date;
  to: Date;
};

type Props = Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue"> & {
  defaultValue: DateRangeType;
  disabled?: boolean;
  onSelect: ({ from, to }: DateRangeType) => void;
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2020; year <= currentYear + 5; year++) {
    years.push(year);
  }
  return years;
};

const generateMonthOptions = () => {
  return [
    { value: 0, label: "1월" },
    { value: 1, label: "2월" },
    { value: 2, label: "3월" },
    { value: 3, label: "4월" },
    { value: 4, label: "5월" },
    { value: 5, label: "6월" },
    { value: 6, label: "7월" },
    { value: 7, label: "8월" },
    { value: 8, label: "9월" },
    { value: 9, label: "10월" },
    { value: 10, label: "11월" },
    { value: 11, label: "12월" },
  ];
};

export function DateRangePicker({
  className,
  defaultValue,
  disabled = false,
  onSelect,
}: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    defaultValue.from || new Date()
  );
  const [hasSelectedStart, setHasSelectedStart] = useState<boolean>(false);

  const yearOptions = generateYearOptions();
  const monthOptions = generateMonthOptions();

  const handleYearChange = (year: string) => {
    const newMonth = new Date(calendarMonth);
    newMonth.setFullYear(parseInt(year));
    setCalendarMonth(newMonth);
  };

  const handleMonthChange = (month: string) => {
    const newMonth = new Date(calendarMonth);
    newMonth.setMonth(parseInt(month));
    setCalendarMonth(newMonth);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          if (!disabled) {
            setOpen(isOpen);
            if (isOpen) {
              setHasSelectedStart(false);
            }
          }
        }}
      >
        <PopoverTrigger asChild>
          <div className="flex justify-around items-center gap-[4px]">
            <Button
              id="date"
              variant={"outline"}
              className={cn(
                "w-[125px] h-[30px] justify-between text-left font-normal text-[10px]",
                !defaultValue && "text-muted-foreground"
              )}
              disabled={disabled}
            >
              {defaultValue.from ? (
                format(defaultValue.from, "yyyy-MM-dd")
              ) : (
                <span>Pick a date</span>
              )}
              <CommonImg
                src={CalendarIcon}
                alt="Calendar"
                className="size-[12px]"
              />
            </Button>
            <span className="text-[10px] text-gray-B font-bold">~</span>
            <Button
              id="date"
              variant={"outline"}
              className={cn(
                "w-[125px] h-[30px] justify-between text-left font-normal text-[10px]",
                !defaultValue && "text-muted-foreground"
              )}
              disabled={disabled}
            >
              {defaultValue.to ? (
                format(defaultValue.to, "yyyy-MM-dd")
              ) : (
                <span>Pick a date</span>
              )}

              <CommonImg
                src={CalendarIcon}
                alt="Calendar"
                className="size-[12px]"
              />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 border-b">
            <div className="flex gap-2 items-center justify-center">
              <Select
                value={calendarMonth.getFullYear().toString()}
                onValueChange={handleYearChange}
              >
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}년
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={calendarMonth.getMonth().toString()}
                onValueChange={handleMonthChange}
              >
                <SelectTrigger className="w-[80px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((month) => (
                    <SelectItem
                      key={month.value}
                      value={month.value.toString()}
                    >
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={calendarMonth}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            selected={defaultValue}
            onSelect={(e) => {
              if (!disabled && e) {
                if (e.from && !e.to) {
                  setHasSelectedStart(true);
                  onSelect({
                    from: e.from,
                    to: e.from,
                  });
                } else if (e.from && e.to) {
                  onSelect({
                    from: e.from,
                    to: e.to,
                  });

                  if (hasSelectedStart) {
                    setOpen(false);
                  }
                  setHasSelectedStart(false);
                }
              }
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
